import React, { InputHTMLAttributes, forwardRef, useState } from 'react';
import { LucideIcon } from 'lucide-react';
import { gsap } from 'gsap';

interface AuthInputProps extends InputHTMLAttributes<HTMLInputElement> {
  label?: string;
  error?: string;
  icon?: LucideIcon;
  iconPosition?: 'left' | 'right';
  id?: string;
}

const AuthInput = forwardRef<HTMLInputElement, AuthInputProps>(
  (
    {
      label,
      error,
      icon: Icon,
      iconPosition = 'left',
      className = '',
      type = 'text',
      id,
      ...props
    },
    ref
  ) => {
    const [isFocused, setIsFocused] = useState(false);
    const inputWrapperRef = React.useRef<HTMLDivElement>(null);
    const iconRef = React.useRef<HTMLDivElement>(null);

    // Generate a unique ID if not provided
    const inputId = id || `input-${Math.random().toString(36).substring(2, 9)}`;

    const handleFocus = () => {
      setIsFocused(true);
      if (iconRef.current) {
        gsap.to(iconRef.current, {
          color: '#0ea5e9',
          duration: 0.3,
        });
      }
    };

    const handleBlur = () => {
      setIsFocused(false);
      if (iconRef.current) {
        gsap.to(iconRef.current, {
          color: '#9ca3af',
          duration: 0.3,
        });
      }
    };

    return (
      <div className='relative'>
        {label && (
          <label
            htmlFor={inputId}
            className={`block text-sm font-medium mb-2 transition-colors duration-300 ${
              error ? 'text-red-400' : isFocused ? 'text-cyan-400' : 'text-gray-300'
            }`}
          >
            {label}
          </label>
        )}

        <div
          ref={inputWrapperRef}
          className={`relative overflow-hidden rounded-lg transition-all duration-300 ${
            error ? 'ring-2 ring-red-500/50' : isFocused ? 'ring-2 ring-cyan-500/30' : ''
          }`}
          style={{
            background: 'rgba(30, 41, 59, 0.5)',
            backdropFilter: 'blur(10px)',
          }}
        >
          {/* Gradient border on focus */}
          {isFocused && !error && (
            <div
              className='absolute inset-0 rounded-lg pointer-events-none'
              style={{
                background:
                  'linear-gradient(135deg, rgba(14, 165, 233, 0.5) 0%, rgba(139, 92, 246, 0.5) 100%)',
                zIndex: -1,
              }}
            />
          )}

          {Icon && iconPosition === 'left' && (
            <div
              ref={iconRef}
              className='absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none text-gray-400'
            >
              <Icon size={18} />
            </div>
          )}

          <input
            ref={ref}
            id={inputId}
            type={type}
            className={`
              w-full py-3 px-4
              text-white placeholder-gray-500
              bg-transparent border-0
              focus:outline-none focus:ring-0
              transition-all duration-300
              ${Icon && iconPosition === 'left' ? 'pl-10' : ''}
              ${Icon && iconPosition === 'right' ? 'pr-10' : ''}
              ${className}
            `}
            onFocus={handleFocus}
            onBlur={handleBlur}
            {...props}
          />

          {Icon && iconPosition === 'right' && (
            <div className='absolute inset-y-0 right-0 pr-3 flex items-center pointer-events-none text-gray-400'>
              <Icon size={18} />
            </div>
          )}
        </div>

        {error && <p className='mt-1 text-sm text-red-400 animate-shake'>{error}</p>}
      </div>
    );
  }
);

AuthInput.displayName = 'AuthInput';

export default AuthInput;
